/**
 * LPC-10289: C2DS Validation Error Parser
 *
 * Parses C2DS validation error messages and maps them to form field keys.
 * C2DS returns semicolon-separated error messages that need to be parsed
 * and displayed inline below the respective form fields.
 */

import { C2DSFieldKey, C2DSFieldError } from "@investorRequest/models";
import { isValidRoutingNumberCheckDigit } from "./SpecificValidations";

// LPC-10289: Re-export shared types so existing imports from this module keep working.
export type { C2DSFieldKey, C2DSFieldError };

// Mapping of C2DS error message patterns to field keys
// Order matters for patterns that could match multiple fields
interface C2DSErrorPattern {
  pattern: RegExp;
  fieldKey: C2DSFieldKey;
  displayName: string;
}

// Error patterns in order of specificity (more specific patterns first)
const C2DS_ERROR_PATTERNS: C2DSErrorPattern[] = [
  // Intermediary fields (check these first as they're more specific)
  {
    pattern: /Intermediary SWIFT ID/i,
    fieldKey: "intermediarySwiftOrAba",
    displayName: "Intermediary SWIFT ID",
  },
  // Main SWIFT ID (must come after Intermediary SWIFT ID check)
  {
    pattern: /(?<!Intermediary\s)SWIFT ID/i,
    fieldKey: "SwiftId",
    displayName: "SWIFT ID",
  },
  // IBAN
  {
    pattern: /IBAN/i,
    fieldKey: "IBAN",
    displayName: "IBAN",
  },
  {
    pattern: /Branch ID/i,
    fieldKey: "BranchId",
    displayName: "Branch ID",
  },
  {
    pattern: /Bank ID/i,
    fieldKey: "BankIdNbr",
    displayName: "Bank ID",
  },
  {
    pattern: /Bank Account/i,
    fieldKey: "bankAccountNumber",
    displayName: "Bank Account",
  },
  // Transit Routing Number - this is tricky as it can appear twice
  // First occurrence = main routingNumber, second = intermediaryRoutingNumber
  {
    pattern: /Transit Routing Number/i,
    fieldKey: "routingNumber",
    displayName: "Routing Number",
  },
];

// Result of parsing C2DS errors
export interface C2DSParseResult {
  fieldErrors: Record<C2DSFieldKey, C2DSFieldError | null>;
  hasErrors: boolean;
  unmatchedMessages: string[];
}

/**
 * LPC-10289: Options for parsing C2DS errors with routing number values
 * Used to intelligently determine which routing number field is invalid
 * when C2DS returns a single "Transit Routing Number" error
 */
export interface C2DSParseOptions {
  routingNumber?: string;
  intermediaryRoutingNumber?: string;
  paymentMethod?: string;
}

/**
 * Appends a message to an existing field error or creates a new one.
 * Multiple messages for the same field are joined with newlines.
 */
function appendFieldError(result: C2DSParseResult, fieldKey: C2DSFieldKey, displayName: string, message: string): void {
  if (result.fieldErrors[fieldKey]) {
    result.fieldErrors[fieldKey]!.message += `\n${message}`;
  } else {
    result.fieldErrors[fieldKey] = { fieldKey, displayName, message };
  }
  result.hasErrors = true;
}

export function parseC2DSErrors(errorResponse: string | null | undefined, options?: C2DSParseOptions): C2DSParseResult {
  const result: C2DSParseResult = {
    fieldErrors: {
      routingNumber: null,
      SwiftId: null,
      IBAN: null,
      BankIdNbr: null,
      BranchId: null,
      bankAccountNumber: null,
      bankCountry: null,
      intermediarySwiftOrAba: null,
      intermediaryRoutingNumber: null,
    },
    hasErrors: false,
    unmatchedMessages: [],
  };

  if (!errorResponse || typeof errorResponse !== "string") {
    return result;
  }

  // Split by semicolon and filter empty strings
  const errorMessages = errorResponse
    .split(";")
    .map((msg) => msg.trim())
    .filter((msg) => msg.length > 0);

  if (errorMessages.length === 0) {
    return result;
  }

  // Track how many times we've seen "Transit Routing Number" errors
  let transitRoutingCount = 0;

  for (const message of errorMessages) {
    let matched = false;

    // Check for Transit Routing Number first (special handling for duplicates)
    if (/Transit Routing Number/i.test(message)) {
      transitRoutingCount++;
      matched = true;

      if (transitRoutingCount === 1) {
        // LPC-10289: When we have only ONE Transit Routing Number error,
        // use check digit validation to determine which field is actually invalid
        const mainRoutingNumber = options?.routingNumber || "";
        const intermediaryRoutingNumber = options?.intermediaryRoutingNumber || "";

        const isMainValid = isValidRoutingNumberCheckDigit(mainRoutingNumber);
        const isIntermediaryValid = isValidRoutingNumberCheckDigit(intermediaryRoutingNumber);

        // If we have both values, determine which one is invalid
        if (mainRoutingNumber && intermediaryRoutingNumber) {
          if (!isMainValid && isIntermediaryValid) {
            appendFieldError(result, "routingNumber", "Routing Number", message);
          } else if (isMainValid && !isIntermediaryValid) {
            appendFieldError(result, "intermediaryRoutingNumber", "Intermediary Routing Number", message);
          } else if (!isMainValid && !isIntermediaryValid) {
            appendFieldError(result, "routingNumber", "Routing Number", message);
            appendFieldError(result, "intermediaryRoutingNumber", "Intermediary Routing Number", message);
          }
        } else if (mainRoutingNumber && !intermediaryRoutingNumber) {
          appendFieldError(result, "routingNumber", "Routing Number", message);
        } else if (!mainRoutingNumber && intermediaryRoutingNumber) {
          appendFieldError(result, "intermediaryRoutingNumber", "Intermediary Routing Number", message);
        } else {
          appendFieldError(result, "routingNumber", "Routing Number", message);
        }
      } else if (transitRoutingCount === 2) {
        appendFieldError(result, "intermediaryRoutingNumber", "Intermediary Routing Number", message);
      }
      continue;
    }

    if (/ABA/i.test(message)) {
      matched = true;
      if (options?.paymentMethod === "swift") {
        appendFieldError(result, "bankCountry", "Bank Country", message);
      } else {
        appendFieldError(result, "routingNumber", "Routing Number", message);
      }
      continue;
    }

    // Check other patterns
    for (const { pattern, fieldKey, displayName } of C2DS_ERROR_PATTERNS) {
      if (fieldKey === "routingNumber") continue;

      if (pattern.test(message)) {
        matched = true;
        appendFieldError(result, fieldKey, displayName, message);
        break;
      }
    }

    if (!matched) {
      result.unmatchedMessages.push(message);
    }
  }

  return result;
}

/**
 * Gets the display message for a C2DS field error.
 * Returns the actual C2DS API error message(s).
 * Multiple messages are separated by newlines.
 */
export function getC2DSErrorDisplayMessage(fieldError: C2DSFieldError | null): string {
  if (!fieldError) return "";

  return fieldError.message;
}

/**
 * Checks if a specific field has a C2DS error.
 */
export function hasC2DSFieldError(
  fieldErrors: Record<C2DSFieldKey, C2DSFieldError | null>,
  fieldKey: C2DSFieldKey
): boolean {
  return fieldErrors[fieldKey] !== null;
}
